"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Info as HelpCircle, ShieldCheck, TrendingUp, BookOpenCheck, Plus, Minus } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const faqItems = [{
  id: "item-1",
  question: "What is the difference between a fixed annuity and a fixed indexed annuity?",
  answer: "A fixed annuity credits interest at a rate set by the insurance company, subject to the guarantees and terms of the contract.\n\nA fixed indexed annuity, or FIA, credits interest in part based on the performance of a market index. Your money is not invested directly in the index. The amount of interest credited depends on the contract's crediting method and may be limited by features such as caps, participation rates, or spreads.\n\nFixed indexed annuities generally provide protection from direct losses caused by a decline in the referenced index. However, withdrawals, surrender charges, contract adjustments, optional rider charges, and other contract provisions may reduce the value you receive.",
  tag: "Core Difference"
}, {
  id: "item-2",
  question: "How do caps, participation rates, and spreads affect my returns?",
  answer: "These features help determine how much index-linked interest may be credited to a fixed indexed annuity.\n\nA cap sets a maximum credited interest rate for a particular crediting period. A participation rate determines what percentage of an index gain is used in the interest calculation. A spread subtracts a stated percentage from the index gain before interest is credited.\n\nThese terms vary by contract and may change at specified times according to the contract. Because of these limits, an FIA generally will not receive the full return of the referenced market index.",
  tag: "Crediting Rules"
}, {
  id: "item-3",
  question: "Can I lose my principal in a fixed indexed annuity?",
  answer: "A traditional fixed indexed annuity generally protects the contract value from direct losses caused solely by a decline in the referenced market index. The contract is not directly invested in the index.\n\nHowever, this does not mean the contract value can never decrease or that every dollar is available at all times. Withdrawals, surrender charges, contract adjustments, optional rider charges, and other contract provisions may affect the amount you receive.\n\nGuarantees are subject to the terms of the annuity contract and the claims-paying ability of the issuing insurance company.",
  tag: "Principal Safety"
}, {
  id: "item-4",
  question: "How liquid is my money in an annuity?",
  answer: "Annuities are generally designed as long-term financial products. Many contracts permit some withdrawals during the accumulation period, but the amount that can be withdrawn without a surrender charge varies by contract.\n\nTaking money out during a surrender period may result in surrender charges or other contract adjustments. Withdrawals may also have federal and state tax consequences. If a taxable distribution is taken before age 59½, an additional 10% federal tax may apply to the taxable portion unless an exception applies.\n\nAlways review the specific contract's withdrawal provisions, surrender schedule, and tax considerations before taking money out.",
  tag: "Liquidity & Rules"
}, {
  id: "item-5",
  question: "What is an income rider and do I need one?",
  answer: "Some annuities offer an optional living-benefit or income rider designed to provide a contractual method for calculating future income. These riders may provide income that can continue for life when their requirements are met.\n\nAn income rider may use a separate benefit or income base to calculate withdrawals. That value is generally not the same as the contract's cash or surrender value and usually cannot be withdrawn as a lump sum.\n\nRider features, charges, withdrawal percentages, and guarantees vary by contract. Whether an income rider is appropriate depends on the individual's income needs, goals, other retirement resources, and the specific annuity being considered.",
  tag: "Retirement Income"
}];
export default function Annuities() {
  const shouldReduceMotion = useReducedMotion();
  const [openId, setOpenId] = useState<string | null>(null);
  const toggleItem = (id: string) => {
    setOpenId(prev => prev === id ? null : id);
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.07
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 14
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: shouldReduceMotion ? 0 : 0.35,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="fixed-fixed-indexed-annuities" data-nav="light" className="bg-background text-foreground py-20 md:py-28 relative overflow-hidden">
      <div className="mx-auto max-w-[1240px] px-5 sm:px-6 md:px-8">
        {/* Section Header */}
        <div className="mx-auto max-w-2xl text-center mb-12 md:mb-16">
          <Badge variant="outline" className="mb-4 bg-muted/60 text-foreground border-border uppercase tracking-widest text-xs font-semibold px-3.5 py-1 rounded-full">
            Annuity Basics
          </Badge>

          <h2 className="text-3xl sm:text-4xl md:text-5xl font-default font-bold text-foreground tracking-tight leading-tight">
            Fixed & fixed indexed annuities
          </h2>

          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">
            Understand how principal protection, interest rate guarantees, and market-linked crediting work together to secure retirement income.
          </p>

          {/* Architectural Anchor Dot Rule */}
          <div className="flex items-center justify-center gap-2 mt-8">
            <span className="size-1.5 rounded-full bg-primary" />
            <span className="h-px w-10 bg-border" />
            <span className="size-1.5 rounded-full bg-primary" />
            <span className="h-px w-10 bg-border" />
            <span className="size-1.5 rounded-full bg-primary" />
            <span className="h-px w-10 bg-border" />
            <span className="size-1.5 rounded-full bg-primary" />
          </div>
        </div>

        {/* Accordion FAQ List */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="mx-auto max-w-[840px] space-y-3.5 mb-14">
          {faqItems.map((item, i) => {
          const isOpen = openId === item.id;
          return <motion.div key={item.id} data-index={i} variants={itemVariants} className="bg-card border border-border rounded-xl overflow-hidden shadow-xs hover:border-primary/40 transition-[border-color,box-shadow] duration-200">
                <Button type="button" variant="ghost" onClick={() => toggleItem(item.id)} aria-expanded={isOpen} className="w-full h-auto flex items-center justify-between gap-4 p-5 sm:p-6 text-left cursor-pointer whitespace-normal font-normal hover:bg-transparent focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring">
                  <div className="flex items-start gap-3.5 sm:gap-4">
                    <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground font-mono text-xs font-medium">
                      0{i + 1}
                    </span>
                    <div>
                      <span className="block text-base sm:text-lg font-default font-semibold text-card-foreground leading-snug">
                        {item.question}
                      </span>
                      <span className="inline-block mt-1 text-xs font-medium text-muted-foreground">
                        {item.tag}
                      </span>
                    </div>
                  </div>

                  <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-muted text-foreground transition-colors duration-150">
                    {isOpen ? <Minus className="size-4" /> : <Plus className="size-4" />}
                  </div>
                </Button>

                {isOpen && <div className="px-5 pb-6 sm:px-6 sm:pb-6 pt-0 border-t border-border/50 text-sm sm:text-base text-muted-foreground font-sans leading-relaxed">
                    <div className="pt-4">{item.answer}</div>
                  </div>}
              </motion.div>;
        })}
        </motion.div>

        {/* Clarity Advisory Callout (Plinth Frame) */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: shouldReduceMotion ? 0 : 0.4,
        ease: "easeOut"
      }} className="mx-auto max-w-[840px] rounded-2xl bg-card border border-border p-6 sm:p-8 md:p-10 shadow-xs">
          <div className="space-y-2.5">
            <div className="flex items-center gap-2 text-foreground font-default font-semibold text-lg">
              <BookOpenCheck className="size-5 text-primary" />
              <span>Need help comparing carriers or rate caps?</span>
            </div>
            <p className="text-sm sm:text-base text-muted-foreground font-sans leading-relaxed">
              We independently analyze fixed rate MYGAs and indexed crediting options across top-rated carriers so you get unbiased clarity on fees, caps, and income riders.
            </p>
          </div>

          <div className="mt-6 pt-6 border-t border-border grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs text-muted-foreground font-sans">
            <div className="flex items-center gap-2">
              <ShieldCheck className="size-4 text-primary shrink-0" />
              <span>100% Principal protection against market losses</span>
            </div>
            <div className="flex items-center gap-2">
              <TrendingUp className="size-4 text-primary shrink-0" />
              <span>Tax-deferred interest accumulation options</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}